import React, { useRef, useEffect } from "react";
import Webcam from "react-webcam";
import { Spin } from "antd";

import Timer from "./components/Timer";
import OrientationGuide from "./components/OrientationGuide";
import CaptureButton from "./components/CaptureButton";
import ErrorView from "./components/ErrorView";
import PermissionView from "./components/PermissionView";
import CameraToggleButton from "./components/CameraToggleButton";
import UploadButton from "./components/UploadButton";

import StandMobileDegree from "./components/StandMobileDegree";

import { CameraProvider, useCameraContext } from "./context/CameraContext";
import { CAMERA_CONFIG } from "./constants";

const CameraComponent: React.FC = () => {
  const webcamRef = useRef<Webcam>(null);
  const {
    state,
    isSupported,
    hasPermission,
    orientation,
    handleCapture,
    handleRequestPermission,
    handleContinueWithoutPermission,
    handleRetry,
    handleGoBack,
    handleUseUpload,
    handleCameraReady,
    handleCameraError,
    handleToggleCamera,
    handleOrientationCorrect,
    handleOrientationIncorrect,
    getCurrentConfig,
  } = useCameraContext();

  useEffect(() => {
    if (state.timerCount === 0 && state.showTimer) {
      handleCapture(webcamRef);
    }
  }, [state.timerCount, state.showTimer]);

  if (state.error) {
    return <ErrorView error={state.error} onRetry={handleRetry} onGoBack={handleGoBack} onUseUpload={handleUseUpload} />;
  }

  if (state.currentStep === "permission") {
    return <PermissionView isLoading={state.permissionLoading} onRequestPermission={handleRequestPermission} onContinueWithoutPermission={handleUseUpload} onGoBack={handleGoBack} />;
  }

  if (state.currentStep === "processing") {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black">
        <div className="text-center text-white">
          <Spin size="large" />
          <p className="mt-4">Processing images...</p>
        </div>
      </div>
    );
  }

  const currentConfig = getCurrentConfig();

  return (
    <div className="fixed inset-0 z-50 bg-black">
      {state.isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black">
          <Spin size="large" />
        </div>
      )}

      <UploadButton onUpload={handleUseUpload} isVisible={!state.isLoading} />

      <div className="absolute z-[60] flex items-center justify-center w-fit h-12 text-white transition-all duration-200 bg-black rounded-full top-4 left-1/2 -translate-x-1/2 bg-opacity-70">
        <span className="px-4 text-white rounded-full l min-w-fit bg-opacity-80">{currentConfig.title}</span>
      </div>

      <CameraToggleButton isVisible={state.hasBackCamera && !state.isLoading} facingMode={state.facingMode} onToggle={handleToggleCamera} />

      <Webcam
        ref={webcamRef}
        audio={false}
        className="object-cover w-full h-full"
        screenshotFormat="image/jpeg"
        mirrored={state.facingMode === "user"}
        videoConstraints={{
          ...CAMERA_CONFIG.VIDEO_CONSTRAINTS,
          facingMode: { ideal: state.facingMode },
        }}
        onUserMedia={handleCameraReady}
        onUserMediaError={handleCameraError}
        key={state.facingMode}
      />

      {((state.captureMode === "auto" && state.isOrientationCorrect) || state.captureMode === "manual") && !state.showTimer && (
        <img src={currentConfig.poseImage} alt="Pose guide" className="absolute z-20 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none top-1/2 left-1/2 max-h-[80dvh] opacity-80" />
      )}

      {state.captureMode === "auto" && isSupported && hasPermission && (
        <div className="absolute inset-0 z-30">
          <StandMobileDegree
            type={state.currentStep as "front" | "side"}
            onSuccess={handleOrientationCorrect}
            onError={handleOrientationIncorrect}
            orientation={orientation}
            isSupported={isSupported}
            isPermissionGranted={hasPermission === true}
            message={currentConfig.degreeText}
          />
        </div>
      )}

      {state.captureMode === "manual" && <OrientationGuide />}

      <Timer />

      {state.captureMode === "manual" && !state.isCapturing && <CaptureButton onCapture={() => handleCapture(webcamRef)} />}
    </div>
  );
};

const OptimizedBodyMeasurementCamera: React.FC = () => {
  return (
    <CameraProvider>
      <CameraComponent />
    </CameraProvider>
  );
};

export default OptimizedBodyMeasurementCamera;
